import { useMemo, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment,
} from '../../hooks/useDepartments';
import { usePeople, useSetPersonDepartment } from '../../hooks/usePeople';
import type { Department } from '../../lib/departmentsApi';
import { Select } from '../ui/Select';
import { useConfirm } from '../ui/ConfirmProvider';

// Settings → "Departamente" (CLE-DASH).
//
// Two-pane layout mirroring LeadsPipelinesSettings: department list + create on
// the left, editor on the right. The editor manages name/color and the
// department's members (people are moved between departments via
// PATCH /api/people/:id/department, which also keeps the legacy name mirror).
// Deleting a department detaches its people and tasks (departmentId → null).

const COLOR_SWATCHES = [
  '#3b82f6',
  '#eab308',
  '#22c55e',
  '#16a34a',
  '#a855f7',
  '#f97316',
  '#ef4444',
  '#64748b',
];

const DEFAULT_COLOR = '#3b82f6';

export function DepartmentsSettings() {
  const { data: departments, isLoading, isError, error } = useDepartments();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = departments?.find((d) => d.id === selectedId) ?? departments?.[0] ?? null;

  return (
    <div className="max-w-4xl">
      <p className="text-sm text-gray-500">
        Configurați departamentele companiei și membrii lor. Sarcinile primesc automat departamentul
        persoanei asignate și pot fi filtrate după departament.
      </p>

      {isLoading && <p className="mt-6 text-sm text-gray-500">Se încarcă departamentele...</p>}
      {isError && (
        <p className="mt-6 text-sm text-red-600">
          Departamentele nu au putut fi încărcate
          {error instanceof Error ? `: ${error.message}` : ''}.
        </p>
      )}

      {departments && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[260px_1fr]">
          <DepartmentList
            departments={departments}
            selectedId={selected?.id ?? null}
            onSelect={setSelectedId}
          />
          {selected ? (
            <DepartmentEditor key={selected.id} department={selected} />
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
              Niciun departament. Creați unul pentru a începe.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Department list + create ------------------------------------------------

function DepartmentList({
  departments,
  selectedId,
  onSelect,
}: {
  departments: Department[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const createDepartment = useCreateDepartment();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    try {
      const created = await createDepartment.mutateAsync({ name: trimmed, color: DEFAULT_COLOR });
      setName('');
      setCreating(false);
      onSelect(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Departamentul nu a putut fi creat.');
    }
  }

  return (
    <div>
      <div className="flex flex-col gap-1">
        {departments.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => onSelect(d.id)}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${
              d.id === selectedId
                ? 'bg-brand-50 font-medium text-brand-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: d.color }}
            />
            <span className="flex-1 truncate">{d.name}</span>
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
              {d.peopleCount}
            </span>
          </button>
        ))}
      </div>

      {creating ? (
        <div className="mt-3 rounded-md border border-gray-200 p-3">
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate();
            }}
            placeholder="Nume departament"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={!name.trim() || createDepartment.isPending}
              className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createDepartment.isPending ? 'Se creează...' : 'Creează'}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setError(null);
                setName('');
              }}
              className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
        >
          <Plus size={15} /> Departament nou
        </button>
      )}
    </div>
  );
}

// --- Department editor (metadata + members) -----------------------------------

function DepartmentEditor({ department }: { department: Department }) {
  const updateDepartment = useUpdateDepartment();
  const deleteDepartment = useDeleteDepartment();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(department.name);
  const [color, setColor] = useState(department.color);

  const dirty =
    (name.trim() !== department.name || color.toLowerCase() !== department.color.toLowerCase()) &&
    name.trim().length > 0;

  async function handleSave() {
    setError(null);
    try {
      await updateDepartment.mutateAsync({
        id: department.id,
        input: { name: name.trim(), color },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Departamentul nu a putut fi salvat.');
    }
  }

  async function handleDelete() {
    setError(null);
    const details =
      department.peopleCount > 0 || department.taskCount > 0
        ? ` ${department.peopleCount} persoane și ${department.taskCount} sarcini vor rămâne fără departament.`
        : '';
    if (
      !(await confirm({
        message: `Ștergeți departamentul "${department.name}"?${details} Această acțiune nu poate fi anulată.`,
        danger: true,
      }))
    )
      return;
    try {
      await deleteDepartment.mutateAsync(department.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Departamentul nu a putut fi șters.');
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-col gap-3 border-b border-gray-100 pb-4">
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Nume departament
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        <div>
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Culoare</span>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-8 w-9 cursor-pointer rounded border border-gray-300"
              aria-label="Culoare departament"
            />
            <div className="flex flex-wrap gap-1">
              {COLOR_SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  style={{ backgroundColor: c }}
                  className={`h-5 w-5 rounded-full ${
                    color.toLowerCase() === c.toLowerCase()
                      ? 'ring-2 ring-gray-400 ring-offset-1'
                      : ''
                  }`}
                  aria-label={`Folosește culoarea ${c}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || updateDepartment.isPending}
            className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Salvează modificările
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteDepartment.isPending}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Trash2 size={14} /> Șterge departament
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
          {error}
        </p>
      )}

      <MembersSection department={department} />
    </div>
  );
}

// --- Members ------------------------------------------------------------------

function MembersSection({ department }: { department: Department }) {
  const { data: people, isLoading } = usePeople();
  const setDepartment = useSetPersonDepartment();
  const [error, setError] = useState<string | null>(null);

  const members = useMemo(
    () => (people ?? []).filter((p) => p.departmentId === department.id),
    [people, department.id],
  );
  const candidates = useMemo(
    () =>
      (people ?? [])
        .filter((p) => p.isActive && p.departmentId !== department.id)
        .map((p) => ({
          value: p.id,
          label: p.fullName,
          description: p.department ?? undefined,
        })),
    [people, department.id],
  );

  function move(personId: string, departmentId: string | null) {
    setError(null);
    setDepartment.mutate(
      { id: personId, departmentId },
      {
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Membrul nu a putut fi mutat.'),
      },
    );
  }

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Membri</h3>
        <span className="text-xs text-gray-400">
          {members.length === 1 ? '1 persoană' : `${members.length} persoane`}
        </span>
      </div>

      {error && (
        <p className="mt-2 rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-600">
          {error}
        </p>
      )}

      {isLoading ? (
        <p className="mt-3 text-sm text-gray-500">Se încarcă persoanele...</p>
      ) : (
        <>
          <ul className="mt-3 flex flex-col gap-1.5">
            {members.length === 0 && (
              <li className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500">
                Niciun membru în acest departament.
              </li>
            )}
            {members.map((p) => (
              <li
                key={p.id}
                className="flex items-center gap-3 rounded-md border border-gray-200 px-3 py-2"
              >
                <span className="flex-1 truncate text-sm font-medium text-gray-800">
                  {p.fullName}
                </span>
                {p.title && <span className="truncate text-xs text-gray-500">{p.title}</span>}
                <button
                  type="button"
                  onClick={() => move(p.id, null)}
                  disabled={setDepartment.isPending}
                  className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-60"
                  aria-label={`Scoate pe ${p.fullName} din departament`}
                >
                  <X size={15} />
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-3 max-w-xs">
            <Select
              label="Adaugă membru"
              hideLabel
              value=""
              onChange={(personId) => {
                if (personId) move(personId, department.id);
              }}
              options={candidates}
              placeholder="Adaugă membru..."
              searchable
              size="sm"
              emptyText="Nicio persoană disponibilă."
            />
          </div>
        </>
      )}
    </div>
  );
}
